/**
 * オファー作成ウィザードの対価行（compensation_lines）まわりの共通ユーティリティ。
 *
 * BE 仕様 (dm-threads.md 2026-05-09 追加):
 *  - `offer_form.compensation_lines` に `{ label, unit_price, quantity, unit }` の配列
 *    を保存する。配列順 = 表示順。
 *  - non-empty で PATCH すると BE 側が `Σ unit_price * quantity` を `thread.contract_amount`
 *    に自動上書きする。空配列の場合は `contract_amount` を直接管理する従来挙動に戻る。
 *
 * FE 側は `OfferCompensationLine` を文字列ベース（編集途中の数値も保持できるため）で
 * 持ち、PATCH 送信前に BE フォーマットへ変換する。表示用の合計値（税抜）も
 * 同じ計算式で算出する。
 */

import { toYenString } from "./money";
import type { UpdateDmThreadParams } from "../app/services/dmThreads";
import type { OfferCompensationLine } from "../components/offer-wizard/types";

/**
 * 対価行の税抜合計を返す。`null` は「合計が計算できる行が 1 件もない」状態を意味し、
 * 呼び出し側は `contract_amount` を送らない判断に使う。
 *
 * 各行は `unit * quantity` で集計し、最終結果を整数に丸める（BE は Decimal で
 * 計算するため小数点以下の差は最終的に BE 計算値で上書きされる）。
 */
export const sumCompensationLines = (
  lines: OfferCompensationLine[] | undefined,
): number | null => {
  if (!lines || lines.length === 0) return null;
  let total = 0;
  let hasAny = false;
  for (const line of lines) {
    const unit = parseFloat(line.unitPrice);
    const qty = parseFloat(line.quantity);
    if (Number.isFinite(unit) && Number.isFinite(qty)) {
      total += unit * qty;
      hasAny = true;
    }
  }
  return hasAny ? Math.round(total) : null;
};

/**
 * 対価行の税抜合計（整数円）。`sumCompensationLines` の `null`（合計対象行なし）を
 * 0 に丸めた版で、残高チェックや小計表示など「数値として必ず欲しい」用途に使う。
 */
export const computeCompensationSubtotalExclusive = (
  lines: OfferCompensationLine[] | undefined,
): number => sumCompensationLines(lines) ?? 0;

/**
 * フォームの対価行を BE 仕様 (`compensation_lines`) のフォーマットへ変換する。
 *
 * `null` を返した場合は「行が無い」状態 = `compensation_lines` を送らない。
 * `unit_price` / `quantity` は文字列のまま渡す（BE 側で Decimal 化される）。
 */
export const mapCompensationLinesToApi = (
  lines: OfferCompensationLine[] | undefined,
): UpdateDmThreadParams["compensation_lines"] | null => {
  if (!lines || lines.length === 0) return null;
  return lines.map((line) => ({
    label: line.label,
    // 円は整数のみ。既存の小数データもここで整数へ正規化して送信する。
    unit_price: toYenString(line.unitPrice),
    quantity: line.quantity,
    unit: line.unit,
  }));
};
